import { assign } from '@xstate/immer';
import { useActor } from '@xstate/react';
import { useContext } from 'react';
import { createMachine, InterpreterFrom } from 'xstate';
import { HatcheningContext } from '../context';
import { RevealedGribble } from '../state-machine';

export type OneRevealContext = {
  gribble: RevealedGribble;
};

type OneRevealEvents =
  | { type: 'revealOne'; id: string }
  | { type: 'revealAll' }
  | { type: 'next' }
  | { type: 'finish' };

export const ONE_REVEAL_MACHINE = 'OneRevealMachine';

export const oneRevealMachine = createMachine(
  {
    tsTypes: {} as import('./one-reveal-machine.typegen').Typegen0,
    schema: { context: {} as OneRevealContext, events: {} as OneRevealEvents },
    id: ONE_REVEAL_MACHINE,
    initial: 'propertiesRevealing',
    states: {
      propertiesRevealing: {
        on: {
          revealAll: {
            target: 'propertiesRevealed',
            actions: 'revealAllProperties',
          },
          revealOne: {
            target: 'propertyReveal',
            actions: 'revealProperty',
          },
        },
      },
      propertyReveal: {
        always: [
          {
            target: 'propertiesRevealed',
            cond: 'allPropertiesRevealed',
          },
          {
            target: 'propertiesRevealing',
          },
        ],
      },
      propertiesRevealed: {
        on: {
          next: {
            target: 'colorsRevealing',
          },
        },
      },
      colorsRevealing: {
        on: {
          revealAll: {
            actions: 'revealAllColors',
            target: 'colorsRevealed',
          },
          revealOne: {
            actions: 'revealColor',
            target: 'colorReveal',
          },
        },
      },
      colorReveal: {
        always: [
          {
            cond: 'allColorsRevealed',
            target: 'colorsRevealed',
          },
          {
            target: 'colorsRevealing',
          },
        ],
      },
      colorsRevealed: {
        on: {
          next: {
            target: 'everythingRevealed',
          },
        },
      },
      everythingRevealed: {
        on: {
          finish: {
            target: 'end',
          },
        },
      },
      // we don't want to end immediately once we've revealed everything
      // to give the user an opportunity to click the finish button to
      // move us to the final state (which brings the user back to egg selection)
      end: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      revealProperty: assign((ctx, payload) => {
        const property = ctx.gribble.properties.find(
          (property) => property.traitType === payload.id,
        );
        if (!property) {
          console.warn(`Attempted to reveal property ${payload.id} but it doesn't exist?`);
          return;
        }
        property.revealed = true;
      }),
      revealAllProperties: assign((ctx) => {
        for (const property of ctx.gribble.properties) {
          property.revealed = true;
        }
      }),
      revealColor: assign((ctx, payload) => {
        const color = ctx.gribble.colors.find((property) => property.traitType === payload.id);
        if (!color) {
          console.warn(`Attempted to reveal color ${payload.id} but it doesn't exist?`);
          return;
        }
        color.revealed = true;
      }),
      revealAllColors: assign((ctx) => {
        for (const color of ctx.gribble.colors) {
          color.revealed = true;
        }
      }),
    },
    guards: {
      allColorsRevealed: (ctx) => {
        return ctx.gribble.colors.every((color) => color.revealed);
      },
      allPropertiesRevealed(ctx) {
        return ctx.gribble.properties.every((property) => property.revealed);
      },
    },
  },
);

export function useOneReveal() {
  // unfortunately we have to reach into the context of the original
  // machine to specifically use a child actor (phrasing lol)
  return useActor(
    useContext(HatcheningContext).children.get(ONE_REVEAL_MACHINE)! as InterpreterFrom<
      typeof oneRevealMachine
    >,
  );
}
